import type { api } from '@udm/contracts';
import { Building2, MapPinned, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ProtocolTag } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';
import { decidesPlacement } from '../sites/PlacementReview';
import { CreateFromProposal } from './CreateFromProposal';
import './proposalCard.css';

/**
 * A proposed commune: where discovered devices sit outside every client area. The superadmin can create a client (or
 * a locality of a client) for it from here, with the commune's area, and adopt its discovered devices.
 */
export function ProposalCard({ p, onClose }: { p: api.AdoptionProposal; onClose: () => void }) {
  const { t } = useTranslation();
  const canCreate = decidesPlacement(useMe().data);
  const [creating, setCreating] = useState(false);
  return (
    <aside className="device-card" aria-label={p.name}>
      <header className="device-card-head">
        <span className="device-card-icon proposal-icon">
          <MapPinned size={20} strokeWidth={1.6} />
        </span>
        <div className="device-card-title">
          <strong className="ellipsis">{p.name}</strong>
          <span className="muted small">
            {[
              p.county,
              p.siruta ? t('sites.uat.proposal', { code: p.siruta }) : null,
              p.kind
                ? t(`map.proposals.kind.${p.kind}` as 'map.proposals.kind.municipality', {
                    defaultValue: p.kind,
                  })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
          <X size={16} />
        </button>
      </header>
      <p style={{ margin: 0 }}>
        {t('map.proposals.devices', { count: p.devices, formatted: formatNumber(p.devices) })}
      </p>
      <dl className="kv small">
        {Object.entries(p.by_protocol).map(([proto, n]) => [
          <dt key={`${proto}-k`}>{proto ? <ProtocolTag protocol={proto} /> : '—'}</dt>,
          <dd key={`${proto}-v`}>{formatNumber(n)}</dd>,
        ])}
      </dl>
      {creating ? (
        <CreateFromProposal p={p} onCancel={() => setCreating(false)} />
      ) : (
        <footer className="device-card-foot proposal-card-foot">
          <span className="muted small">{t('map.proposals.hint')}</span>
          <div className="toolbar">
            <Link to="/discovered" className="btn">
              {t('map.proposals.openDiscovered')}
            </Link>
            {canCreate && (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                <Building2 size={15} /> {t('map.proposals.create.open')}
              </button>
            )}
          </div>
        </footer>
      )}
    </aside>
  );
}
